import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import PrincipalsAndUserGroupsDTOType from '@/Communication/PrincipalsAndUserGroupsDTO';
import UserGroupDTO from '@/Communication/UserGroupDTO';
import LocatorHelper from '@/Utility/LocatorHelper';
import { GridColumn, IRoleAssignmentListViewModelParameters } from '#/DefaultViewExtensionInterfaces';
import { RoleAssignmentValue } from '#/CoreFrameInterfaces';

interface DataItem {
    caption: string; 
    selectAll?: boolean;
    isAllPrincipals?: boolean;
    principalId: number;
    parentId?: number;
}

export default class RoleAssignmentListViewModel extends ControlViewModel {

    /*
    * Der Name des Moduls
    */
    public name: string;

    /*
     * Die selektierten Werte
     */
    public values: KnockoutObservable<RoleAssignmentValue[]>;

    /*
     * Das PrincipalsAndUserGroupsDTO
     */
    public PrincipalsAndUserGroupsDTO: KnockoutObservable<PrincipalsAndUserGroupsDTOType>;

    /*
    * Die Spaltendefinitionen
    */
    public columns: KnockoutObservable<GridColumn[]>;

    /*
    * Beinhaltet die Datenquelle
    */
    public dataSource: KnockoutObservableArray<DataItem>;

    /*
    * Ein Observable zur Benachrichtigung über eine Aktualisierung
    */
    public redraw: KnockoutObservable<boolean>;

    /*
     * Das ViewModel für das RoleAssignmentList
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params: IRoleAssignmentListViewModelParameters) {
        super(params);
        
        this.name = this.paramOrDefault(params.name, null);
        this.values = this.observableOrDefault(params.values, null);
        this.undisposeProperties.push('values');
        this.PrincipalsAndUserGroupsDTO = this.observableOrDefault(params.PrincipalsAndUserGroupsDTO, null);
        this.columns = this.createColumns();
        this.dataSource = this.observableOrDefault([]) as KnockoutObservableArray<DataItem>;

        this.redraw = this.observableOrDefault(params.redraw, false).extend({ notify: 'always' });

        this.readData();
        // Aktualisiert die Datenquelle sobald die Mandanten oder Gruppen verändert werden
        this.defineSubscription(this.PrincipalsAndUserGroupsDTO, () => {
            this.readData();
        });
    }

    private createColumns(): KnockoutObservable<GridColumn[]> {
        return this.defineComputed(() => {
            let result: GridColumn[] = [];
            const PrincipalsAndUserGroupsDTO = this.PrincipalsAndUserGroupsDTO();
            const UserGroups = this.getCurrentUserGroups(PrincipalsAndUserGroupsDTO.UserGroups);

            result.push({
                name: 'principal',
                field: 'caption',
                title: ResourceManager.getInstance().get('ROLE_ASSIGNMENT_PRINCIPALS_AND_ROLES'),
                hidden: false,
                width: '200px',
                filterable: false,
                value: null,
            });

            for (let i = 0; i < UserGroups.length; i++) {
                const userGroup = UserGroups[i];

                const principalIds = PrincipalsAndUserGroupsDTO.Principals.map((p) => { return p.PDO_ID; });
                const controlledCheckboxes = principalIds.map((p) => { return `cb_${userGroup.PDO_ID}_${p}`; });

                result.push({
                    name: userGroup.Name,
                    field: `_${userGroup.Name}`,
                    title: userGroup.DisplayName,
                    hidden: false,
                    filterable: false,
                    value: null,
                    width: '120px',
                    additionalWidth: 5, // Für Border oder Sonstiges
                    uiGenerator: (cell, row) => {
                        const disabled = this.isEnabled() ? '' : 'disabled';
                        if ((userGroup.IsAllPrincipals && row.principalId != -1)
                            || (!userGroup.IsAllPrincipals && row.principalId == -1))
                            return '';
                        else if (row.selectAll)
                            return `<center><input id="cb_all_${userGroup.PDO_ID}" type="checkbox" class="checkbox" ${disabled} data-group="${userGroup.PDO_ID}" aria-controls="${controlledCheckboxes
                                }" aria-checked="mixed" data-locator="${LocatorHelper.buildLocator(this.locator, row.caption, userGroup.Name)()}"></center>`;
                        else
                            return `<center><input id="cb_${userGroup.PDO_ID}_${row.principalId}" type="checkbox" class="checkbox" ${disabled} data-principal="${row.principalId}" data-group="${userGroup.PDO_ID}" data-locator="${LocatorHelper.buildLocator(this.locator, row.caption, userGroup.Name)()}" ${cell ? 'checked ' : ''
                                }></center>`;
                    }
                });
            }

            return result;
        });
    }

    private readData(): void {
        let result: DataItem[] = [];
        const PrincipalsAndUserGroupsDTO = this.PrincipalsAndUserGroupsDTO();
        const UserGroups = this.getCurrentUserGroups(PrincipalsAndUserGroupsDTO.UserGroups);
        const Principals = PrincipalsAndUserGroupsDTO.Principals || [];

        const global: DataItem = {
            caption: ResourceManager.getInstance().get('ROLE_ASSIGNMENT_GLOBAL_ASSIGNMENT'),
            principalId: -1,
            parentId: null
        };

        const selectAll: DataItem = {
            caption: ResourceManager.getInstance().get('ROLE_ASSIGNMENT_SELECT_ALL'),
            selectAll: true,
            principalId: -2,
            parentId: null
        };

        for (let i = 0; i < Principals.length; i++) {
            const Principal = Principals[i];

            const object: DataItem = {
                caption: Principal.Name + ' - ' + Principal.FullName,
                principalId: Principal.PDO_ID,
                parentId: Principal.ParentPDO_ID
            };

            for (let j = 0; j < UserGroups.length; j++) {
                const UserGroup = UserGroups[j];

                global[`_${UserGroup.Name}`] = this.isGlobalChecked(UserGroup.PDO_ID.toString());
                global.isAllPrincipals = true;

                object[`_${UserGroup.Name}`] = this.isChecked(UserGroup.PDO_ID.toString(), Principal.PDO_ID.toString());
                object.isAllPrincipals = false;

                selectAll[`_${UserGroup.Name}`] = true;
            }

            result.push(object);
        }

        if (result.length > 0) {
            result.splice(0, 0, global);
            result.splice(1, 0, selectAll);
        }

        this.dataSource(result);
    }

    /*
    * Wird aufgerufen sobald eine Checkbox verändert wird
    */
    public onCheck(principalId: string, groupId: string, isChecked: boolean): void {
        const values = this.values();
        let valueFound = false;

        for (let i = 0; i < values.length; i++) {
            if (values[i].group == groupId && values[i].principal == principalId) {
                valueFound = true;
                values[i].checked = isChecked;
            }
        }

        if (valueFound === false && isChecked) {
            values.push({
                checked: isChecked,
                member: 0,
                principal: principalId,
                group: groupId
            });
        }

        this.values(values);
    }

    /*
    * Hilfsfunktion zum Auslesen der benötigten UserGroups
    */
    private getCurrentUserGroups(UserGroups: Array<UserGroupDTO>): Array<UserGroupDTO> {
        const grouped = _.groupBy(UserGroups, (UserGroup) => {
            return UserGroup.IsAllPrincipals ? 'All' : 'Single';
        });
        const sorted = _.sortBy(grouped.All, 'DisplayName').concat(_.sortBy(grouped.Single, 'DisplayName'));
        return sorted;
    }

    /*
    * Hilfsfunktion zur Bestimmung, ob ein Element selektiert ist
    */
    private isGlobalChecked(groupId: string): boolean {
        const values = this.values.peek();

        for (const item of values) {
            if (item.group === groupId && parseInt(item.principal) == -1)
                return item.checked;
        }

        return false;
    }

    /*
    * Hilfsfunktion zur Bestimmung, ob ein Element selektiert ist
    */
    private isChecked(groupId: string, principalId: string): boolean {
        const values = this.values.peek();

        for (const item of values) {
            if (item.group === groupId && item.principal == principalId)
                return item.checked;
        }

        return false;
    }
}

ComponentManager.setViewModel('RoleAssignmentListViewModel', RoleAssignmentListViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');